import React, { useEffect, useState, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import CustomTable from 'src/components/App/AppGrid/CustomTable/CustomTable';
import {
  Avatar,
  Chip,
  Box,
  alpha,
  Typography,
  DialogContent,
  DialogActions,
  styled,
  Tooltip,
} from '@mui/material';
import Welcome from '../components/patient/Welcome';
import theme from 'src/config/theme';
import { ReactComponent as Edit2 } from '../../../assets/images/edit-2.svg';
import { ReactComponent as Close } from '../../../assets/images/close-circle.svg';
import { ReactComponent as Add } from '../../../assets/images/add-square.svg';
import CustomCard from 'src/modules/app/components/CustomCard/AppointmentCard';
import { useNavigate } from 'react-router-dom';
import NoDataFound from '../components/NoDataFound/NoDataFound';
import { useDispatch, useSelector } from 'react-redux';
import { getPatientRequest } from '../../../modules/app/store/appActions';
import storage from 'src/utils/storageUtils';
import RightSidebar from '../../../components/App/RIghtSidebar/RightSidebar';
import PatientCard from '../components/CustomCard/PatientCard';
import useResponsive from 'src/components/hooks/useResponsive';
import AddPatient from '../components/LefSidebarForms/AddPatient/AddPatient';
import DialogBox from 'src/components/shared/Dialog/DialogBox';

export const AvatarIcon = styled(Avatar)({
  width: '64px',
  height: '64px',
  padding: '10px',
  borderRadius: '40px',
  background: 'linear-gradient(0deg, rgba(150, 57, 57, 0.18), rgba(150, 57, 57, 0.18)), #FFFFFF',
  marginBottom: '5px',
});
const columns = [
  { id: 'patientId', label: 'Patient ID', sx: { '& .css-s1474d': { justifyContent: 'flex-Start !important', fontWeight: 'bold' } } },
  {
    id: 'patient',
    label: 'Patient Name',
    sx: { '& .css-s1474d': { justifyContent: 'flex-Start !important' } },
  },
  { id: 'age', label: 'Age', sx: { '& .css-axw7ok': { justifyContent: 'center !important' } } },
  { id: 'sex', label: 'Sex', sx: { '& .css-axw7ok': { justifyContent: 'center !important' } } },
];


const PatientContainer = () => {
  const { isMobile, isDesktop, isTablet } = useResponsive();

  const [isCardView, setIsCardView] = useState(false);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [patients, setPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState();

  const fetchPatients = useCallback(
    (page = 0, perPage = 10) => {
      const token = storage.get('TOKEN');
      const fetchData = async () => {
        setIsLoading(true);
        try {
          const params = {
            page: page + 1,
            limit: perPage,
          };
          const response = await new Promise((resolve, reject) =>
            dispatch(getPatientRequest({ token, params }, resolve, reject)),
          );
          if (response?.data && response?.meta) {
            setPatients(response.data);
            setTotalCount(response.meta.totalRecords);
          }
        } finally {
          setIsLoading(false);
        }
      };
      fetchData();
    },
    [dispatch],
  );

  useEffect(() => {
    fetchPatients(page, perPage);
  }, [fetchPatients, page, perPage]);

  const handleSearch = useCallback(
    (value) => {
      const token = storage.get('TOKEN');
      const fetchData = async () => {
        setIsLoading(true);
        try {
          const params = value ? { search: value } : null;
          console.log('Params received in getPatient:', params);
          const response = await new Promise((resolve, reject) =>
            dispatch(getPatientRequest({ token, params }, resolve, reject)),
          );

          console.log('search response', response);
          if (response) {
            setPatients(response.data);
          }
        } finally {
          setIsLoading(false);
        }
      };

      fetchData();
    },
    [dispatch],
  );
  const data = useMemo(() => {
    if (!patients || !patients.length) return [];
    return patients.map((patient) => {
      let displayAge = patient?.detailedAge?.ageString;
      if (!displayAge && patient?.dateOfBirth) {
        const birth = dayjs(patient.dateOfBirth);
        const now = dayjs();
        const years = now.diff(birth, 'year');
        const months = now.diff(birth.add(years, 'year'), 'month');
        const days = now.diff(birth.add(years, 'year').add(months, 'month'), 'day');
        displayAge = `${years} ${years === 1 ? 'Year' : 'Years'} ${months} ${months === 1 ? 'Month' : 'Months'} ${days} ${days === 1 ? 'Day' : 'Days'}`;
      } else if (!displayAge && patient?.age) {
        displayAge = `${patient.age} ${patient.age === 1 ? 'Year' : 'Years'}`;
      }

      return {
        patientId: (
          <Tooltip title="Click to copy ID" placement="top" arrow>
            <Chip 
              label={patient.patientId || patient.id} 
              size="small" 
              onClick={() => {
                navigator.clipboard.writeText(patient.patientId || patient.id);
              }}
              sx={{ 
                backgroundColor: alpha(theme.palette.primary.main, 0.08), 
                color: theme.palette.primary.main, 
                fontWeight: 600, 
                borderRadius: '6px',
                padding: '2px 4px',
                cursor: 'copy',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.15),
                  transform: 'scale(1.05)'
                }
              }} 
            />
          </Tooltip>
        ),
        realDbId: patient.id,
        avatarInitial: patient?.fullName?.charAt(0).toUpperCase() || 'N',
        name: patient?.fullName,
        patient: (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateX(4px)',
            }
          }}>
            <Avatar
              sx={{ 
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.7)} 100%)`, 
                color: theme.palette.gery.white,
                boxShadow: `0 4px 10px ${alpha(theme.palette.primary.main, 0.25)}`,
                fontWeight: 600,
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'scale(1.1) rotate(5deg)' }
              }}
            >
              {patient?.fullName?.charAt(0).toUpperCase() || 'N'}
            </Avatar>
            <span style={{ fontWeight: 500, color: theme.palette.gery.black }}>{patient?.fullName || 'N/A'}</span>
          </Box>
        ),
        age: displayAge || 'N/A',
        sex: patient?.gender
          ? `${patient.gender.charAt(0).toUpperCase() + patient.gender.slice(1)}`
          : 'N/A',

      };
    });
  }, [patients]);

  const actions = [
    { label: 'Edit', icon: <Edit2 />, onClick: (row) => alert(`Editing ${row.patient}`) },
    {
      label: 'Cancel', icon: <Close width={isMobile || isTablet ? 18 : 24}
        height={isMobile || isTablet ? 18 : 24} />, onClick: (row) => alert(`Editing ${row.patient}`)
    },
  ];
  return (
    <>
      <Welcome
        category="patients"
        setIsCardView={setIsCardView}
        isCardView={isCardView}
        updatePatients={fetchPatients}
        handleSearch={handleSearch}
      />
      <Box sx={{ mb: 16 }} />

      <Box
        sx={{
          height: 'calc(100vh - 240px)',
          margin: 2,
          boxShadow: isMobile || isTablet || isCardView || isCardView ? 'none' : theme.shadows[1],
          borderRadius: '10px',
          border: isMobile || isTablet || isCardView || isCardView ? 'none' : `1px solid ${alpha(theme.palette.gery.black, 0.1)}`,
          backgroundColor: isMobile || isTablet || isCardView ? 'none' : theme.palette.gery.white,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: (isCardView) && Array.isArray(data) && data.length === 0 ? 'center' : 'space-between',
          alignItems: (isMobile || isTablet || isCardView) && Array.isArray(data) && data.length === 0 ? 'center' : 'none',
          position: 'relative',
          maxHeight: 'calc(100vh - 240px)',
          transition: 'box-shadow 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          animation: 'fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
          '@keyframes fadeInUp': {
            '0%': { opacity: 0, transform: 'translateY(40px) scale(0.98)' },
            '100%': { opacity: 1, transform: 'translateY(0) scale(1)' }
          },
          '&:hover': {
            boxShadow: isMobile || isTablet || isCardView ? 'none' : '0 12px 32px rgba(0,0,0,0.08)',
          }
        }}
      >
        {(isMobile || isTablet || isCardView) ? (
          data.length === 0 ? (
            <NoDataFound
              title="No Patient Found!"
              buttonText="Add Patient"
              buttonIcon={Add}
              onClick={handleOpen}
            />
          ) : (
            <PatientCard
              page={page}
              perPage={perPage}
              setPage={setPage}
              totalCount={totalCount}
              data={data}
            />
          )
        ) : (
          <CustomTable
            disabled={true}
            key={page}
            columns={columns}
            data={data}
            actions={actions}
            page={page}
            onPageChange={(newPage) => {
              setPage(newPage);
            }}
            onPerPageChange={(newPerPage) => {
              setPerPage(newPerPage);
              setPage(0);
            }}
            perPage={perPage}
            totalCount={totalCount}
            loading={isLoading}
            noDataComponent={
              <NoDataFound
                title="No Patient Found!"
                buttonText="Add Patient"
                buttonIcon={Add}
                onClick={handleOpen}
              />
            }
          />
        )}
      </Box>
      <RightSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <DialogBox open={open} onClose={handleClose} >
        <AddPatient onClose={handleClose} updatePatients={fetchPatients} />
      </DialogBox>

    </>
  );
};

export default PatientContainer;
